import React from "react";
import store from "../state/store";
import _ from "underscore";
import BacklogItems from "./backlog/index";
import Enquiry from "./enquiry/show";
import Lead from "./lead/show";
import Modal from "./modal";
import Emailer from "./emailer/show";
import Whatsapper from "./whatsapper/show";
import Pipeline from "./pipeline/index";
import TasksCenter from "./tasks_center/index";
import NavbarSearch from "./navbar/search";
import NavbarNotifications from "./navbar/notifications/index";
import ScheduledCalls from "./scheduled_calls/index";
import BacklogClaimButton from "./backlog/claim_button";
import RoleBadge from "./navbar/role_badge";
import { AppSidebar } from "@/components/app-sidebar";
import { Badge } from "@/components/ui/badge";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";

// The old top navbar's work cluster (claim button, global search, role badge,
// tallies, scheduled calls, notifications) — now a slim header above the body
// in both layout modes.
class WorkHeader extends React.Component {
  componentDidMount() {
    document.addEventListener("keydown", this.handleGlobalKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.handleGlobalKeyDown);
  }

  handleGlobalKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "k") {
      e.preventDefault();
      if (this.searchComponent) this.searchComponent.focus();
    }
  };

  onClaim = (queue, params = {}) => {
    store.emit("backlog_items:claim_one", queue, params);
  };

  render() {
    const sales_manager = this.props.user.sales_manager;
    const tallies = sales_manager ? sales_manager.tallies : null;

    return (
      <header className="flex h-navbar w-full shrink-0 items-center gap-2 border-b border-border bg-background px-4">
        <BacklogClaimButton
          backlog={this.props.backlog}
          loading_unclaimed_backlog={this.props.ui.loading_unclaimed_backlog}
          onClick={this.onClaim}
        />
        <div className="mr-2 min-w-[450px]">
          <NavbarSearch search={this.props.search} ref={(ref) => (this.searchComponent = ref)} />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <RoleBadge sales_manager={sales_manager} />
          {sales_manager ? (
            <Badge variant="secondary" title="Daily claim goal">
              {tallies.daily_claims}/15 claims
            </Badge>
          ) : null}
          {sales_manager ? (
            <Badge variant="secondary" title="Daily handover goal">
              {tallies.daily_handovers}/2 handovers
            </Badge>
          ) : null}
          <ScheduledCalls
            scheduled_calls={this.props.scheduled_calls}
            ui={this.props.ui.scheduled_calls}
            store={store}
          />
          <NavbarNotifications ui={this.props.ui.notifications} />
        </div>
      </header>
    );
  }
}

// Shadcn chrome wrapped around the store-driven body.
function Shell({ user, ui, backlog, search, scheduled_calls, children }) {
  return (
    <TooltipProvider>
      <SidebarProvider>
        <AppSidebar user={user} ui={ui} />
        <SidebarInset className="h-svh overflow-hidden">
          <WorkHeader
            user={user}
            ui={ui}
            backlog={backlog}
            search={search}
            scheduled_calls={scheduled_calls}
          />
          <div className="flex min-h-0 flex-1 flex-col">{children}</div>
        </SidebarInset>
      </SidebarProvider>
      <Toaster richColors closeButton position="top-right" duration={3000} />
    </TooltipProvider>
  );
}

class App extends React.Component {
  componentDidMount() {
    store.emit("backlog_items:index", { filter: "my_tasks" });
  }

  onCloseMailer = () => {
    store.emit("ui:mailer:close");
  };

  onCloseWhatsapper = () => {
    store.emit("ui:whatsapper:close");
  };

  renderBacklogItems() {
    if (!_.isEmpty(this.props.backlog)) {
      return (
        <BacklogItems
          backlog={this.props.backlog}
          selected_item={this.props.backlog_item_selection}
        />
      );
    }

    return null;
  }

  renderItem() {
    if (this.props.enquiry) {
      return (
        <div className="pb-[70px]">
          <Enquiry
            enquiry={this.props.enquiry}
            learners={this.props.enquiry.learners}
            learner_subjects={this.props.enquiry.learner_subjects}
            lesson_plans={this.props.enquiry.lesson_plans}
            subjects={this.props.enquiry.subjects}
            tutor_results={this.props.tutor_results}
            tutor_proposals={this.props.tutor_proposals}
            pricing_calculator={this.props.pricing_calculator}
            orders={this.props.orders}
            change_applied={this.props.change_applied}
            ui={this.props.ui}
          />
        </div>
      );
    } else if (this.props.lead) {
      return (
        <div className="pb-[70px]">
          <Lead lead={this.props.lead} ui={this.props.ui} store={store} />
        </div>
      );
    }
  }

  render() {
    const ui = this.props.ui;
    const isPipeline = ui.main_view === 'pipeline';
    const isTasks = ui.main_view === 'tasks';
    // *_view === 'detail' is the user's intent — render the detail slot even
    // if the entity is still loading (e.g. first paint after reload with ?e=…
    // in the URL). Otherwise we'd flash the list for one frame before the
    // fetch resolves.
    const expectingDetailInPipeline = isPipeline && ui.pipeline_view === 'detail';
    const expectingDetailInTasks = isTasks && ui.tasks_view === 'detail';

    let body;
    if (expectingDetailInPipeline || expectingDetailInTasks) {
      body = (
        <section className="flex-1 min-h-0 overflow-y-auto bg-muted">
          {this.renderItem()}
        </section>
      );
    } else if (isPipeline) {
      body = (
        <div className="flex-1 min-h-0 overflow-y-auto bg-muted">
          <Pipeline pipeline={this.props.pipeline} />
        </div>
      );
    } else if (isTasks) {
      body = (
        <div className="flex-1 min-h-0 overflow-y-auto bg-muted">
          <TasksCenter current_user={this.props.user} tasks_overview={this.props.tasks_overview} />
        </div>
      );
    } else {
      body = (
        <div className="flex flex-1 min-h-0 overflow-hidden bg-muted">
          <aside className="dark [color-scheme:dark] flex h-full w-72 shrink-0 flex-col bg-slate-800 text-foreground">
            {this.renderBacklogItems()}
          </aside>
          <section className="flex-1 min-w-0 h-full overflow-y-auto bg-muted">
            {this.renderItem()}
          </section>
        </div>
      );
    }

    return (
      <div
        className={
          "sales-portal" +
          (isPipeline ? " sales-portal--pipeline" : "") +
          (isTasks ? " sales-portal--tasks" : "")
        }
      >
        <Shell
          user={this.props.user}
          ui={ui}
          backlog={this.props.backlog}
          search={this.props.search}
          scheduled_calls={this.props.scheduled_calls}
        >
          {body}
        </Shell>
        {ui.mailer.open ? (
          <Modal onClose={this.onCloseMailer}>
            <Emailer
              related_to_id={ui.mailer.related_to_id}
              related_to_type={ui.mailer.related_to_type}
              template_name={ui.mailer.template_name}
              templates={ui.mailer.templates}
              email={ui.mailer.email}
              onClose={this.onCloseMailer}
            />
          </Modal>
        ) : null}
        {ui.whatsapper.open ? (
          <Modal title="Send WhatsApp" onClose={this.onCloseWhatsapper}>
            <Whatsapper
              related_to_id={ui.whatsapper.related_to_id}
              related_to_type={ui.whatsapper.related_to_type}
              recipient_id={ui.whatsapper.recipient_id}
              recipient_type={ui.whatsapper.recipient_type}
              template_name={ui.whatsapper.template_name}
              templates={ui.whatsapper.templates}
              whatsapp={ui.whatsapper.whatsapp}
              onClose={this.onCloseWhatsapper}
            />
          </Modal>
        ) : null}
      </div>
    );
  }
}

export default App;
